// Helpers for deriving video thumbnails for workspace resources.
//
// Three sources, in priority order at render time:
//   1. A static, URL-derived still — YouTube exposes one at a predictable URL
//      from the video id, so no upload/og-fetch is needed.
//   2. A poster we generate at upload time for direct video files, stored next
//      to the object at "<videoKey>.thumb.jpg" (see FileUpload). No DB column —
//      the URL is derivable from the video URL via uploadedThumbUrl().
//   3. (Fallback, handled in the component) the browser's first-frame poster
//      from a <video> element, for older uploads with no generated poster.

const YOUTUBE_RE =
  /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/;
const DIRECT_VIDEO_RE = /\.(mp4|mov|webm|m4v|ogv)(?:[?#]|$)/i;
const OTHER_VIDEO_HOST_RE = /(vimeo\.com|loom\.com|wistia\.|wistia\.com)/i;

export function getYouTubeId(url = "") {
  const match = String(url || "").match(YOUTUBE_RE);
  return match ? match[1] : null;
}

// A static image URL for the video, when one can be derived without a network
// round-trip. Currently YouTube only; returns null otherwise.
export function getVideoThumbnailUrl(url = "") {
  const youTubeId = getYouTubeId(url);
  if (youTubeId) {
    return `https://img.youtube.com/vi/${youTubeId}/hqdefault.jpg`;
  }
  return null;
}

export function isDirectVideoUrl(url = "") {
  return DIRECT_VIDEO_RE.test(String(url || ""));
}

// The conventional location of an upload-time generated poster: alongside the
// video object with a ".thumb.jpg" suffix. Strips any query/hash so it matches
// the key FileUpload writes to. Returns null for non-http(s) URLs.
export function uploadedThumbUrl(url = "") {
  const clean = String(url || "").split("#")[0].split("?")[0];
  if (!/^https?:\/\//i.test(clean)) return null;
  return `${clean}.thumb.jpg`;
}

// Should this resource render as a video (and therefore get a thumbnail)?
// Detection is URL-based on purpose — the admin-chosen `type` ("Video") is
// free-form and unreliable, and embeds carry their iframe HTML in `url`.
export function isVideoResource(resource) {
  const url = resource?.url || "";
  if (!url) return false;
  return (
    Boolean(getYouTubeId(url)) ||
    OTHER_VIDEO_HOST_RE.test(url) ||
    isDirectVideoUrl(url)
  );
}
